import type { User } from '@voge/shared';
import { Activity, ChevronsLeft, ChevronsRight, LogOut, Palette } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { logoUrl, useBrandingStatus } from '../../features/branding/useBranding';
import { ThemeToggle } from '../../features/theme/ThemeToggle';
import { NAV_GROUPS, NAV_ITEMS } from '../navigation';

interface SidebarProps {
  user: User;
  onNavigate?: () => void;
  onLogout: () => void;
  /** Свёрнутое меню — только иконки. */
  collapsed?: boolean;
  /** Кнопка сворачивания рендерится, только если передан обработчик. */
  onToggleCollapse?: () => void;
}

export function Sidebar({ user, onNavigate, onLogout, collapsed = false, onToggleCollapse }: SidebarProps) {
  const visibleItems = NAV_ITEMS.filter((item) => user.permissions[item.module]?.view);
  const { data: branding } = useBrandingStatus();
  const customLogo = logoUrl(branding);

  return (
    <div
      className={`flex h-full flex-col border-r border-surface-border bg-surface-raised transition-[width] duration-200 ${
        collapsed ? 'w-16' : 'w-[280px]'
      }`}
    >
      {/* Шапка: лого + название + кнопка сворачивания */}
      <div
        className={`flex py-4.5 ${collapsed ? 'flex-col items-center gap-3 px-2' : 'items-center gap-2.5 px-4'}`}
      >
        {customLogo ? (
          <img src={customLogo} alt="Логотип" className="h-9 w-9 shrink-0 rounded-xl object-contain" />
        ) : (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-accent to-brand-violet text-white shadow-lg shadow-accent/20">
            <Activity size={18} />
          </div>
        )}
        {!collapsed && (
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-sm font-bold tracking-wide text-slate-900 dark:text-slate-50">VOGE DMS</div>
            <div
              title="Dealer Management System"
              className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide text-accent-soft"
            >
              Dealer Management System
            </div>
          </div>
        )}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            className={`shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-surface-overlay hover:text-slate-900 dark:text-slate-500 dark:hover:text-slate-100 ${
              collapsed ? '' : 'ml-auto'
            }`}
          >
            {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          </button>
        )}
      </div>

      <nav className={`flex-1 overflow-y-auto py-2 ${collapsed ? 'space-y-2 px-2' : 'space-y-5 px-3'}`}>
        {NAV_GROUPS.map((group) => {
          const items = visibleItems.filter((i) => i.group === group.id);
          if (items.length === 0) return null;
          return (
            <div key={group.id}>
              {collapsed ? (
                <div className="mx-2 mb-1 border-t border-surface-border/70" />
              ) : (
                <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink
                    key={item.module}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={onNavigate}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }: { isActive: boolean }) =>
                      `flex items-center rounded-lg text-sm transition-colors ${
                        collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
                      } ${
                        isActive
                          ? 'bg-accent/10 font-medium text-accent-soft'
                          : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'
                      }`
                    }
                  >
                    <item.icon size={17} className="shrink-0" />
                    {!collapsed && item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className={`border-t border-surface-border ${collapsed ? 'p-2' : 'p-3'}`}>
        <div className="mb-2">
          <ThemeToggle collapsed={collapsed} />
        </div>
        {user.role === 'admin' && (
          <NavLink
            to="/settings/branding"
            onClick={onNavigate}
            title={collapsed ? 'Оформление' : undefined}
            className={({ isActive }: { isActive: boolean }) =>
              `mb-2 flex items-center rounded-lg text-sm transition-colors ${
                collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
              } ${
                isActive
                  ? 'bg-accent/10 font-medium text-accent-soft'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'
              }`
            }
          >
            <Palette size={17} className="shrink-0" />
            {!collapsed && 'Оформление'}
          </NavLink>
        )}
        {collapsed ? (
          <button
            type="button"
            onClick={onLogout}
            title={`Выйти — ${user.name}`}
            className="flex w-full items-center justify-center rounded-lg py-2.5 text-slate-400 transition hover:bg-white/5 hover:text-brand-red"
            aria-label="Выйти"
          >
            <LogOut size={17} />
          </button>
        ) : (
          <div className="flex items-center justify-between rounded-lg px-2 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{user.name}</p>
              <p className="truncate text-xs text-slate-500">{user.email}</p>
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="rounded-md p-2 text-slate-400 transition hover:bg-white/5 hover:text-brand-red"
              aria-label="Выйти"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
